import type { SeenMedplumClient } from '@seen/core/client'
import {
  isQuestionnaireAnswerOp,
  isValidatedToolOp,
  type StagedSuggestion,
} from '@seen/core/models/compositionNote'
import { ExtendedCondition } from '@seen/core/models/conditions/ExtendedCondition'
import { referenceStringToId } from '@seen/core/references'
import type { EditorEvents } from '@tiptap/core'
import type { Editor } from '@tiptap/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { runReconcileSweep } from '#src/components/rich-text-editor/acceptSuggestionPipeline.ts'
import {
  AI_PROPOSALS_PLUGIN_KEY,
  type AIProposal,
  toAIProposal,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { isWholeDocReplace } from '#src/components/rich-text-editor/aiProposalTransactions.ts'
import {
  isSuggestionContentPresent,
  narrowPersistedOp,
} from '#src/components/rich-text-editor/aiSuggestionReconcile.ts'
import { collectHtmlReferencedConditions } from '#src/components/rich-text-editor/conditionMentionSideEffects.ts'
import {
  clinicianDropReason,
  droppedSuggestionContent,
} from '#src/components/rich-text-editor/droppedSuggestionDetails.ts'
import { getIslandRegistry } from '#src/components/rich-text-editor/islandRegistry.ts'
import {
  conditionInactiveDropReason,
  conditionMissingDropReason,
  conditionNotOnChartDropReason,
  isConditionDropReason,
} from '#src/components/rich-text-editor/suggestionDropReasons.ts'
import { isUndoRedoOperation } from '#src/components/rich-text-editor/transactionOrigin.ts'
import { resolveOperation } from '#src/contexts/CollaborativeEditorRegistry.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import type { SuggestionPersistence } from '#src/hooks/useSuggestionPersistence.ts'

// Undo/redo classification comes from the ONE shared classifier
// (transactionOrigin.ts) — this hook used to carry its own key list, and the
// two silently diverged (`y-sync$1` and bare `history$` were only matched by
// one of them). Direction is derived from content presence at the call sites,
// never from the meta: absent = undo, absent→present transition = redo.

/**
 * A persisted-pending suggestion that could not be placed this mount — its
 * target no longer resolves (field deleted, heading renamed, value not in
 * options) or its content fails the validation gate. Surfaced in the dropped-
 * suggestions dialog with a per-item reject; the record stays pending (a later
 * mount may resolve it) until the clinician acts.
 */
export interface DroppedSuggestion {
  /** The staging record id, for a per-item reject via the reject projection. */
  id: string
  /** Where the suggestion would have gone, e.g. `Insert after "…"`. */
  label: string
  /** Clinician-facing reason — never a resolveOperation/model-facing message. */
  reason: string
  /**
   * The suggestion's own text, flattened out of HTML. What the clinician
   * actually needs to see and copy; absent for an op that writes no content of
   * its own (a checkbox toggle) and on drops raised before the record is known.
   */
  content?: string
  /**
   * Set only while this record resolves cleanly against the CURRENT document
   * again — the clinician deleted the duplicate sentence that made its anchor
   * ambiguous, or retyped the text that had gone missing. Calling it paints the
   * suggestion and drops it from this list; its presence is what the status bar
   * counts, and its absence is what makes a row's original reason still true.
   */
  place?: () => void
}

/*
 * Wires an editor to its persisted AI suggestions (design B2 §2/§3):
 *
 * 1. HYDRATION (Q3): once the editor is synced/ready AND the resource read has
 *    resolved (`persistence.isResourceLoaded`), re-resolve every PENDING
 *    persisted record against the CURRENT doc and re-paint its decoration
 *    (reusing the record id + provenance.toolCallId so clearByToolCall and the
 *    reconciler still line up). Decided records are skipped. A record whose
 *    target no longer resolves keeps its decoration off but stays pending, and
 *    is counted into `droppedCount` for the status bar ("N could not be
 *    placed"). One-shot per (editor instance, resourceRef, contentResetCount) so
 *    collab-tx flurries don't thrash — but the one-shot only latches after the
 *    resource is loaded: on a cold cache (page reload) the editor becomes ready
 *    before the FHIR read returns, and latching on that early pass would consume
 *    the one-shot with zero records, so persisted suggestions would never paint
 *    (the 2026-06-05 reload regression). And it re-arms when the host bumps
 *    `contentResetCount` after a whole-doc reset, whose single whole-document
 *    ReplaceStep makes the collapse guards drop every painted proposal (the
 *    2026-06-05 paint-then-wipe regression).
 *
 * 2. RECONCILER (Q8): on every docChanged transaction, reconcile each known
 *    record against current CONTENT — an accepted record whose content is now
 *    absent (undone) → pending (re-add its decoration); a redo that
 *    re-materializes it → accepted again (drop the decoration, re-apply the FHIR
 *    side effects). Content-derived, so undo/redo drive the flip with no bespoke
 *    undo listener. Status flips are handed to the DEBOUNCED projection (one FHIR
 *    patch per settle window), so the persisted status is a projection of editor
 *    content, never an imperative click-patch (the constraint that keeps cmd+z
 *    honest). FIRST acceptance is not inferred here at all: the accept pipeline
 *    holds the exact record id the clinician acted on and writes `accepted`
 *    itself, so a pending record whose content merely turns up stays pending.
 *
 * No-ops entirely (returns 0) when there is no resource (`resourceRef`
 * undefined) — every persistence method is empty in that case.
 * @param editor - The TipTap editor instance, or null before mount.
 * @param isEditorReady - True once the editor is synced/ready for hydration.
 * @param resourceRef - The edited resource's reference; undefined when there is
 *   no resource to persist staging for (the hook then no-ops).
 * @param persistence - The resource's staging persistence facade.
 * @param contentResetCount - Host generation counter for whole-doc content
 *   resets (detailed inline below).
 * @param revealed - Per-tab "review apparatus is active" flag for deferred
 *   paint (detailed inline below).
 * @param revealGen - Per-tab reveal generation for hide → re-reveal re-arms
 *   (detailed inline below).
 * @returns The pending records that couldn't be placed this mount, each with
 *   its reason, for the status bar's interactive dropped list to surface.
 */
/*
 * One paint pass: re-resolve every still-pending persisted record against the
 * CURRENT doc and (re-)add its decoration. Idempotent — the plugin map keys by
 * id — and resurrection-safe: `getLatestStatus` reads the latest persisted
 * status from the in-memory staging `Basic`, so a record whose decide is still
 * in flight (the note's `pending` list is then stale) is never re-painted.
 * Returns the records that could not be placed, each with its reason, for the
 * status bar's interactive dropped list.
 */
// A reveal-time drop, described for the clinician: what the suggestion said,
// where it would have gone, and why it didn't land.
function droppedFor(
  editor: Editor,
  record: StagedSuggestion,
  noteSuggestionsEnabled: boolean,
  citationMentionsEnabled: boolean
): DroppedSuggestion {
  return {
    id: record.id,
    label: record.label ?? 'Suggestion',
    content: droppedSuggestionContent(record),
    reason: clinicianDropReason({
      editor,
      record,
      noteSuggestionsEnabled,
      citationMentionsEnabled,
    }),
  }
}

function paintPendingProposals(
  editor: Editor,
  persistence: SuggestionPersistence,
  noteSuggestionsEnabled: boolean,
  citationMentionsEnabled: boolean,
  // Restrict the pass to these record ids. Used for the per-proposal Strategy B
  // rescue, which must not re-resolve proposals Strategy A just placed
  // correctly — content re-resolution is the weaker question (it misplaces onto
  // a duplicate and false-drops a co-edited anchor).
  only?: ReadonlySet<string>
): DroppedSuggestion[] {
  const proposals: AIProposal[] = []
  const dropped: DroppedSuggestion[] = []
  for (const record of persistence.pendingSuggestions) {
    if (only && !only.has(record.id)) {
      continue
    }
    /* Dispatch by op type: validated-tool and questionnaire-answer suggestions
     * are NOT editor decorations — their node views read the pending record
     * straight off the note for their own accept UX (that note read is why
     * Braden suggestions always survived reloads). Skip them here so they
     * neither paint nor count as "could not be placed"; without this a
     * questionnaire proposal narrows to null below and is reported to the
     * clinician as a suggestion that could not be placed. */
    if (isValidatedToolOp(record.op) || isQuestionnaireAnswerOp(record.op)) {
      continue
    }
    const latest = persistence.getLatestStatus(record.id)
    if (latest !== undefined && latest !== 'pending') {
      continue
    }
    const op = narrowPersistedOp(record.op)
    if (!op) {
      dropped.push(
        droppedFor(
          editor,
          record,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      )
      continue
    }
    const resolved = resolveOperation(
      editor,
      op,
      noteSuggestionsEnabled,
      citationMentionsEnabled
    )
    if ('error' in resolved) {
      /* Target no longer resolves, or the content fails the gate: keep the
       * decoration off but keep the record pending — a later mount may resolve
       * it. Never auto-reject; surface it on the dropped list. `resolved.error`
       * is deliberately discarded — it is the model's retry message, naming the
       * operation type — and re-derived as clinician copy. */
      dropped.push(
        droppedFor(
          editor,
          record,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      )
      continue
    }
    /* The op supplies the PLACEMENT; the clinician's island edit, when there is
     * one, supplies the CONTENT the card previews and a re-opened island seeds
     * from — the same content the accept pipeline would insert. */
    /* The narrowed op rides along so a later revision of this proposal can
     * build its own persisted op from it — a rehydrated proposal is revisable
     * exactly like one staged this turn. */
    proposals.push(
      toAIProposal(
        record.id,
        record.provenance.toolCallId,
        resolved,
        record.label,
        record.editedContent,
        op
      )
    )
  }
  if (proposals.length > 0) {
    editor.commands.addAIProposals(proposals)
  }
  return dropped
}

// Painted proposals whose positions are still known-stale — i.e. the ones
// `settleAIProposals` could not re-derive from their relative anchor.
function staleProposalIds(editor: Editor): Set<string> {
  const ids = new Set<string>()
  const state = AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)
  for (const [id, proposal] of state?.proposals ?? []) {
    if (proposal.stale) {
      ids.add(id)
    }
  }
  return ids
}

/*
 * Which of the dropped records would place cleanly against the CURRENT doc —
 * asked with the same `resolveOperation` the paint pass asks, so a record only
 * counts as recovered on evidence the paint would act on.
 *
 * Condition-validity drops are excluded on purpose: no edit to the note's text
 * can bring back a condition that left the chart or stopped being active, so
 * placing one would resurrect a clinically-invalid suggestion. They are also
 * appended by an effect keyed on the candidate signature, which will not re-fire
 * to re-add one that got pruned from the list here.
 */
function recoveredDropIds(
  editor: Editor,
  dropped: DroppedSuggestion[],
  persistence: SuggestionPersistence,
  noteSuggestionsEnabled: boolean,
  citationMentionsEnabled: boolean
): Set<string> {
  const recovered = new Set<string>()
  for (const entry of dropped) {
    if (isConditionDropReason(entry.reason)) {
      continue
    }
    const record = persistence.pendingSuggestions.find(
      (candidate) => candidate.id === entry.id
    )
    if (!record || isValidatedToolOp(record.op)) {
      continue
    }
    const op = narrowPersistedOp(record.op)
    if (!op) {
      continue
    }
    const probe = resolveOperation(
      editor,
      op,
      noteSuggestionsEnabled,
      citationMentionsEnabled
    )
    if (!('error' in probe)) {
      recovered.add(entry.id)
    }
  }
  return recovered
}

/*
 * Seed the reconciler's per-record presence baseline from the CURRENT doc.
 *
 * `presenceRef` holds the synchronous last-tick presence that both of the
 * reconciler's transition guards compare against, and it starts EMPTY. Without a
 * seed, the first undo after a page load reads `wasPresent === undefined` for
 * every record, so a genuine undo-of-accept would be refused its demotion and the
 * record would sit at `accepted` over a doc that no longer holds its content —
 * with its encounter link stranded. Hydration is where the baseline can be taken
 * honestly: it runs only once the resource has loaded AND the collaborative doc
 * has synced, so it reads the real document rather than a pre-hydration empty
 * one. Every KNOWN record is seeded, not just the pending ones the paint walks —
 * accepted records are precisely the ones the demotion guard reads.
 *
 * Deliberately NOT folded into `paintPendingProposals`: the reconciler also calls
 * that from inside the transaction handler (the synchronous undo repaint), and
 * seeding there would overwrite last-tick presence with post-undo presence — the
 * very transition the guard looks for would be erased in the tick before it is
 * read.
 *
 * Indeterminate (`null`) presence is not recorded, mirroring the reconciler.
 */
function seedPresence(
  editor: Editor,
  persistence: SuggestionPersistence,
  presence: Map<string, boolean>
): void {
  const all = persistence.getAllSuggestions()
  for (const record of all) {
    if (isValidatedToolOp(record.op)) {
      continue
    }
    const present = isSuggestionContentPresent(editor, record.op, record, all)
    if (present !== null) {
      presence.set(record.id, present)
    }
  }
}

export function useAISuggestionHydration(
  editor: Editor | null,
  isEditorReady: boolean,
  resourceRef: string | undefined,
  persistence: SuggestionPersistence,
  // The ai-note-suggestions gate (required, no default — see
  // mentionContractFor). Threaded to every resolveOperation call below so a
  // pending condition-mention record can't re-paint via hydration/reconcile
  // when off; every other pending record type is unaffected.
  noteSuggestionsEnabled: boolean,
  // The ai-citation-mentions gate (required, no default — see
  // mentionContractFor). Threaded alongside noteSuggestionsEnabled to every
  // resolveOperation call below so a pending citation-mention record can't
  // re-paint via hydration/reconcile when off.
  citationMentionsEnabled: boolean,
  /**
   * Generation counter the HOST editor bumps after each mount-lifecycle
   * whole-doc content reset (its seed / Medplum-reconcile `setContent`).
   * y-prosemirror reports `setContent` as ONE whole-document ReplaceStep, so
   * the AIProposals collapse guards (correctly) drop every painted proposal —
   * the 2026-06-05 live wipe: hydration painted all records, then the
   * reconcile reset silently dropped them in the same commit. Each bump
   * re-arms the one-shot below so still-pending records are re-resolved and
   * re-painted against the post-reset doc. Defaults to 0 for hosts that never
   * reset content.
   */
  contentResetCount = 0,
  /**
   * Per-tab "the suggestion review apparatus is ACTIVE in this tab" flag (S5
   * deferred paint). Defaults to `true`, so every caller that doesn't opt into
   * deferral — and every non-deferred mount — behaves exactly as before.
   *
   * `CollaborativeEditor` passes `false` ONLY for the deferred-paint case: a
   * note mounted WITH pre-existing staged suggestions, where we want a banner
   * instead of an auto-painted load view. While `false`, all three effects
   * below (hydration paint, content reconciler, decided-sweep) are dormant and
   * NO decorations are painted — there's nothing to reconcile or sweep yet. The
   * banner's "Review suggestions" flips this to `true`, which re-runs hydration
   * (it's a dependency) and activates the reconciler + sweep — from that point
   * behavior is identical to today's auto-paint-on-mount.
   *
   * LIVE generation is unaffected: a fresh note loads with no staging, so the
   * host latches `revealed = true` at load and live decorations paint via the
   * registry's `addAIProposals` path with the reconciler/sweep already active.
   */
  revealed = true,
  /**
   * Per-tab "reveal generation" the host bumps each time it RE-reveals after a
   * Hide (task #4). The hydration one-shot is keyed by this generation, so a
   * hide → reveal cycle re-arms a fresh paint pass: Hide tears the decorations
   * down (the host calls the remove-proposals path), leaving the plugin map
   * empty, and without a re-arm the latch — already consumed for this
   * `(resourceRef, contentResetCount, remoteWipeGen)` — would never re-paint when
   * `revealed` flips back true. Bumping `revealGen` on each reveal makes the
   * re-reveal re-resolve every still-pending record against the current doc and
   * re-paint, idempotently (hydration consults `getLatestStatus` so a record
   * decided meanwhile is not resurrected). Defaults to 0 for hosts that never
   * hide/re-reveal.
   */
  revealGen = 0,
  // Fired for a record whose accept was UNDONE (accepted→pending on a Cmd+Z), so
  // the host can compensate the accept's FHIR side effects — under the no-create
  // model, unlink the encounter-diagnosis links it created. Runs from a
  // synchronous transaction handler, so the callback owns its own async work.
  onUndoFlipCompensate?: (record: StagedSuggestion) => void,
  /**
   * FHIR client for the Option-C reveal-time condition-mention validation pass
   * (detailed at the effect below). Optional and trailing so every existing
   * positional caller — and every note without condition mentions — is
   * unaffected; when omitted the validation effect is dormant and behavior is
   * exactly today's paint-only hydration.
   */
  medplum?: SeenMedplumClient,
  /* True once the collaborative (Liveblocks/Yjs) doc has completed its initial
   * server sync — i.e. the note body's content has actually arrived. Distinct
   * from `isEditorReady`, which the host derives from a signal that is also true
   * during "synchronizing" (content still in flight). The hydration paint and the
   * reconcile sweep gate on THIS so neither reads a pre-hydration empty/stale doc
   * on a cold reload — the sweep especially, which would otherwise read a
   * committed accept as orphaned and destructively unlink it. Trailing with a
   * `true` default so every existing positional caller — and the unit tests,
   * which supply a fully-materialized editor doc — behave exactly as before; only
   * the collaborative host threads the real provider signal. */
  isDocSynchronized = true,
  // Fired for a record whose accept was REDONE (pending→accepted on a
  // Cmd+Shift+Z), so the host can re-apply the accept's FHIR side effects the
  // intervening undo tore down — relink the encounter + restore the receipt.
  // Distinct from a fresh accept, whose pipeline already ran the side effects:
  // only a redo transaction (isUndoRedoOperation, with the content now present)
  // reaches this. Runs from a synchronous transaction handler, so the callback
  // owns its own async work.
  onRedoFlipReapply?: (record: StagedSuggestion) => void,
  /**
   * The note's participant (`Patient/{id}`) for the reveal-time condition
   * validation pass, which un-paints a pending mention whose condition belongs
   * to a DIFFERENT patient's chart — the same ownership check the accept-time
   * validator runs, applied at paint so a wrong-chart suggestion never reads as
   * acceptable. Omitted/null skips the check (non-participant editors, tests).
   */
  patientRef?: string | null,
  /*
   * Per-editor "repaint generation" the host bumps when it re-pends records
   * that are already decided in THIS editor instance, which today means one
   * thing: "Undo rejection of all". Reject All removes the decorations, so the
   * undo has nothing to restore — re-projecting the records to `pending` grows
   * `pendingSuggestions`, but that list is not part of the hydration one-shot's
   * key, so without a bump the latch stays consumed and nothing repaints until
   * a reload builds a fresh editor. Same lever as `revealGen`, for the same
   * reason (decorations torn down under a still-consumed latch); kept separate
   * because reveal is host-owned state whose bump also flips `revealing` and
   * flashes the banner's loading view. The repaint is idempotent for the same
   * reason a re-reveal is: hydration consults `getLatestStatus`, so a record
   * decided meanwhile is not resurrected.
   */
  repaintGen = 0
): DroppedSuggestion[] {
  const [dropped, setDropped] = useState<DroppedSuggestion[]>([])

  /* Persistence in a ref so the per-transaction reconciler reads the latest
   * note/accessors without re-subscribing on every render. */
  const persistenceRef = useRef(persistence)
  persistenceRef.current = persistence

  /* Per-record content presence at the LAST reconcile tick, keyed by record id.
   * Synchronous and derived from editor.state.doc, so — unlike the async
   * React-Query-backed record.status — it is always current. The reconciler uses
   * it to detect a genuine absent→present TRANSITION (a redo that re-materialized
   * THIS record's content), so the redo re-apply fires ONLY on a real
   * redo-of-this-accept, never on an unrelated undo/redo that leaves an
   * already-accepted chip present throughout (which would spuriously re-read the
   * condition/encounter + churn the editor on every keystroke-undo). */
  const presenceRef = useRef(new Map<string, boolean>())

  /* Per-record status BASELINE, the status counterpart of presenceRef: the last
   * flip THIS hook pushed for a record, held until the server agrees. Both of the
   * reconciler's branches read it instead of `record.status`.
   *
   * `record.status` cannot be trusted for a decision taken in the same breath as
   * the write that changes it. It comes from `getAllSuggestions()` → the
   * `useSearchOne`-backed staging Basic, and a PATCH only INVALIDATES that search
   * key (nothing writes it directly), so a flip becomes visible two round trips
   * later — PATCH, then search refetch. One accept queues three serialized
   * PATCHes, and an undo inside that window read `record.status === 'pending'`
   * for a record the hook had already flipped to accepted: neither branch fired,
   * the queued accepted write landed anyway, and the record stayed accepted over
   * a document that no longer held its content. The promotion branch's delta-guard
   * below already refused to trust `record.status` for the same reason; this makes
   * the refusal symmetric.
   *
   * Trusting the projection is safe because it is not a guess: every entry
   * corresponds to a flip already enqueued on `useSuggestionPersistence`'s
   * SERIALIZED write chain, so it is the value the record will settle to. An entry
   * is dropped as soon as the server reports that value (below), after which the
   * next read baselines from `record.status` again — so a record decided in
   * another tab, or a record read fresh after a reload, is never judged against a
   * stale local projection. The one disagreement this can't reconcile is a
   * `rejected` arriving from elsewhere while a projection is outstanding; the loop
   * skips rejected records before it reads the projection at all, leaving status
   * untouched rather than guessing. */
  const projectedStatusRef = useRef(
    new Map<string, StagedSuggestion['status']>()
  )

  /* Scoped to one resource, mirroring useSuggestionPersistence's own write-chain
   * reset: the projections describe writes queued for the PREVIOUS resource's
   * records and mean nothing for the next one's. */
  // resourceRef is the reset key — the effect must rerun when the resource changes even though the callback never reads it.
  useEffect(() => {
    projectedStatusRef.current.clear()
  }, [resourceRef])

  /* Remote-wipe generation (build-spec §5): y-prosemirror reports ANY
   * Yjs-origin change — a remote co-editor edit, a collab undo, a sync tick —
   * as ONE whole-document ReplaceStep, under which the AIProposals collapse
   * guards (correctly, by design — do NOT weaken them) drop every painted
   * proposal. The HOST only bumps `contentResetCount` for its OWN setContent
   * resets, so remote wipes previously erased the paint until the next full
   * reload. The transaction listener below detects whole-doc replaces and
   * bumps this generation (debounced, so a remote typing flurry collapses
   * into one re-hydration); the latch key carries it, preserving the
   * exactly-once-per-generation discipline. Re-painting is safe against
   * resurrection because hydration consults `getLatestStatus` (the latest
   * persisted status, read from the in-memory staging `Basic`) before painting
   * a record. */
  /* mountedRef guards the debounced setState against any post-unmount path:
   * cancel() calls clearTimeout(), but Node.js's event loop can snapshot a
   * timer for firing before clearTimeout() runs (loaded CI runners hit this).
   * The ref is the correctness guarantee regardless of which post-unmount path
   * actually fires the trailing edge. */
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  /* Has THIS resource's collaborative doc ever finished syncing?
   *
   * `isDocSynchronized` is the provider's LIVE status, and it drops back to
   * "synchronizing" whenever local updates are in flight — which a burst of
   * local edits guarantees. Measured live: an "Accept all" unsubscribed the
   * reconciler after its first accept and left it unsubscribed through the
   * second accept AND the Cmd+Z that followed, so the undo's present→absent
   * transition was never observed. The record the clinician undid kept its
   * `accepted` status over a note that no longer held its content, with its
   * encounter link uncompensated and no card offering it back.
   *
   * What the gate is actually for is the PRE-hydration doc — a cold reload where
   * the content has not arrived and a committed accept would read as orphaned.
   * That is a one-way condition, so latch it: before the first sync the
   * reconciler stays dormant exactly as before; after it, the doc is real and
   * stays real, and an in-flight local update is no reason to stop watching it.
   * Keyed by resource so switching notes re-arms the wait. */
  const syncedResourceRef = useRef<string | undefined>(undefined)
  if (isDocSynchronized && resourceRef !== undefined) {
    syncedResourceRef.current = resourceRef
  }
  const hasDocSynced =
    isDocSynchronized ||
    (resourceRef !== undefined && syncedResourceRef.current === resourceRef)

  const [remoteWipeGen, setRemoteWipeGen] = useState(0)
  const bumpRemoteWipeGen = useDebouncedCallback(() => {
    if (mountedRef.current) {
      setRemoteWipeGen((g) => g + 1)
    }
  }, 400)
  // Cancel on unmount so prod never fires a wasted re-render when the
  // clearTimeout race is won; mountedRef above is the correctness guarantee
  // when the race is lost (trailing edge fires into a torn-down environment).
  useEffect(() => () => bumpRemoteWipeGen.cancel(), [bumpRemoteWipeGen])

  // ---- Hydration: one-shot per (editor, resourceRef, content-reset generation) ----
  /* Keyed by editor INSTANCE (WeakMap), not just resourceRef: a recreated editor
   * (collab reconnect while the component stays mounted) starts with empty
   * plugin state and needs its own hydration pass; an already-hydrated
   * instance must never re-paint. The key also carries `contentResetCount`:
   * a host whole-doc reset wipes the painted proposals (collapse guards), so
   * the post-reset generation needs a fresh pass — while within one
   * generation the latch still guarantees exactly-once. */
  const hydratedEditorsRef = useRef(new WeakMap<Editor, string>())
  const isResourceLoaded = persistence.isResourceLoaded
  useEffect(() => {
    /* `isResourceLoaded` gates the latch: before the resource read resolves,
     * `pendingSuggestions` is transiently empty, and consuming the one-shot on
     * that pass would skip hydration forever (cold-cache reload). It is also a
     * dependency, so the effect re-runs when the read resolves after the
     * editor is already ready — either arrival order hydrates exactly once.
     *
     * `revealed` defers the paint (S5): while a resource mounted into
     * pre-existing staging is unrevealed, this effect no-ops (the banner shows
     * instead); clicking "Review suggestions" flips `revealed` true and re-runs
     * it, so the one-shot latch is consumed only once the paint is actually
     * wanted.
     *
     * `isDocSynchronized` gates the same way as the reconcile sweep below: on a
     * cold reload `isEditorReady` is true while the Yjs doc is still empty, so
     * latching the one-shot then would resolve every op against an empty doc,
     * mark them all "could not be placed," and — when nothing later re-arms the
     * key (multi-user / no-drift-solo room, where content arrives via Yjs with no
     * host setContent) — never re-paint. Waiting for the doc to actually sync
     * paints against real content; it is a dependency, so the effect re-runs when
     * the content lands. */
    if (
      !editor ||
      !isEditorReady ||
      !isDocSynchronized ||
      !resourceRef ||
      !isResourceLoaded ||
      !revealed
    ) {
      return
    }
    const key = `${resourceRef}#${contentResetCount}#${remoteWipeGen}#${revealGen}#${repaintGen}`
    if (hydratedEditorsRef.current.get(editor) === key) {
      return
    }
    hydratedEditorsRef.current.set(editor, key)

    /* Baseline the reconciler's transition guards against the just-synced doc
     * (detailed at seedPresence) BEFORE the first transaction can be observed. */
    seedPresence(editor, persistenceRef.current, presenceRef.current)

    /* Nothing here re-offers an ACCEPTED record, and that absence is the
     * invariant: once the clinician has clicked accept the suggestion is
     * decided, and it is never painted again — not on this mount, not on any
     * later one. A mount pass cannot tell an accept the fast-undo race stranded
     * from one whose inserted text the clinician deliberately deleted
     * afterwards, and re-offering the second case silently overrides a decision
     * they made on purpose (live symptom, 2026-08-18: an accepted condition
     * mention was deleted by hand and the next reload resurrected it as a
     * pending card).
     *
     * Undo is the one thing that takes an accept back, and the reconciler owns
     * that — a live present→absent transition under an undo/redo op, OBSERVED
     * rather than inferred from a reload. The clinical linkage an abandoned
     * accept leaves behind is not stranded either: the reconcile sweep unlinks
     * orphaned Encounter.diagnosis refs off the receipt's own createdRefs,
     * independently of any status flip (see runReconcileSweep). */

    setDropped(
      paintPendingProposals(
        editor,
        persistenceRef.current,
        noteSuggestionsEnabled,
        citationMentionsEnabled
      )
    )
  }, [
    editor,
    isEditorReady,
    isDocSynchronized,
    resourceRef,
    isResourceLoaded,
    revealed,
    contentResetCount,
    remoteWipeGen,
    revealGen,
    repaintGen,
    noteSuggestionsEnabled,
    citationMentionsEnabled,
  ])

  // ---- Reconciler: content-derived status projection per docChanged tx ----
  /* Gated on `revealed` (S5): while a deferred note is unrevealed nothing is
   * painted, so there's no decoration to reconcile; activating only on reveal
   * keeps the dormant tab from projecting status flips. After reveal it runs
   * exactly as before.
   *
   * Gated on the doc having synced for the same reason as the paint effect above
   * and the reconcile sweep below — this is the third reader of the same
   * document and the only one that also drives FHIR writes. Before the room
   * syncs, `editor.state.doc` is transiently empty or behind: a committed
   * accept's chip reads absent, so an unrelated local undo would flip the
   * record back to pending and fire the unlink compensation against a document
   * that never lost the chip.
   *
   * On the LATCHED signal, not the provider's live status (see hasDocSynced):
   * this effect must stay subscribed across the clinician's own edits, and the
   * live status goes "synchronizing" while those are being pushed. Unlike the
   * paint and the sweep — one-shots that simply run later if they are skipped —
   * this one watches for TRANSITIONS, so a gap in its subscription is not a
   * delay, it is a permanently missed undo. */
  useEffect(() => {
    if (!editor || !resourceRef || !revealed || !hasDocSynced) {
      return
    }
    const onTransaction = ({
      transaction,
    }: EditorEvents['transaction']): void => {
      if (!transaction.docChanged) {
        return
      }
      /* Whole-doc replace (build-spec §5): a remote co-editor edit, a collab
       * undo/redo, a sync tick, or the host's own setContent all arrive as one
       * whole-document ReplaceStep that collapses every painted proposal's PM
       * anchor.
       *
       * Strategy A first: `settleAIProposals` re-derives each proposal that
       * carries a Yjs relative anchor from that anchor (the extension held it
       * stale-but-painted through the collapse — no flash), repositioning the
       * structurally-recoverable ones. It runs in the same task as the wiping
       * transaction, AFTER y-prosemirror reconciled its binding, so it
       * re-resolves against the settled doc. Every proposal it places is
       * authoritative — those are NOT re-asked as a content question, which
       * would false-drop a remotely-edited anchor word or silently re-anchor
       * onto a duplicate (the differentiators relative positions exist to
       * survive). What it could not place falls to the scoped Strategy B rescue
       * below.
       *
       * Fallback (no relative anchors — a non-collab editor, or proposals staged
       * before this feature): re-arm content re-resolution exactly as before —
       * synchronous repaint on a local undo (anti-flash), debounced re-hydration
       * otherwise so a remote typing flurry re-hydrates once. */
      if (isWholeDocReplace(transaction)) {
        /* Invariant 6: flush any OPEN+dirty island draft to editedContent BEFORE
         * repositioning or dropping proposals. A reposition keeps the island
         * open (no unmount → its on-close flush never fires) and a drop can
         * finalize the record, so without this synchronous flush a mid-edit
         * draft still in the island's 500ms debounce window is lost or read
         * stale. Self-guarding per island (no-op when clean/decided) and
         * idempotent. */
        getIslandRegistry(editor)?.flushOpenIslands()
        const repositioned = editor.commands.settleAIProposals()
        /* Strategy B, per proposal: re-resolve exactly the ones Strategy A left
         * stale from their own persisted op, and drop only what neither
         * strategy can place. `settleAIProposals` carries the reasoning for why
         * an anchor that no longer re-derives is not evidence its target left
         * the note — the case that unpainted an undecided sibling on a redo. */
        const unplaced = staleProposalIds(editor)
        if (unplaced.size > 0) {
          const lost = paintPendingProposals(
            editor,
            persistenceRef.current,
            noteSuggestionsEnabled,
            citationMentionsEnabled,
            unplaced
          )
          for (const id of staleProposalIds(editor)) {
            editor.commands.rejectAIProposal(id) // removes decoration only
          }
          /* APPEND, never replace: the scoped pass only looked at `unplaced`,
           * so its return is not the whole dropped set. Only the pass's own
           * drops are surfaced — a proposal left stale because its record was
           * decided in another tab is skipped by the pass and unpainted by the
           * loop above, and belongs on no dropped list. */
          if (lost.length > 0) {
            setDropped((prev) => {
              const seen = new Set(prev.map((d) => d.id))
              const additions = lost.filter((entry) => !seen.has(entry.id))
              return additions.length === 0 ? prev : [...prev, ...additions]
            })
          }
        } else if (!repositioned) {
          if (isUndoRedoOperation(transaction)) {
            setDropped(
              paintPendingProposals(
                editor,
                persistenceRef.current,
                noteSuggestionsEnabled,
                citationMentionsEnabled
              )
            )
          } else {
            bumpRemoteWipeGen()
          }
        }
      }
      const p = persistenceRef.current
      const records = p.getAllSuggestions()
      if (records.length === 0) {
        return
      }
      const flips: { id: string; status: 'accepted' | 'pending' }[] = []
      const reAdd: AIProposal[] = []
      // Computed once: this transaction is an undo/redo op (either direction —
      // y-prosemirror tags both). Load-bearing for the redo path below, which
      // must NOT read the async-lagged record.status.
      const isUndoRedoOp = isUndoRedoOperation(transaction)
      for (const record of records) {
        /* Read from `record.status`, deliberately NOT the projection: this hook
         * never projects `rejected`, so a rejected record is one decided
         * elsewhere (the node view, another tab) and must be left alone even
         * when a projection of ours is still outstanding for it. */
        if (record.status === 'rejected') {
          continue
        }
        /* ASYMMETRY (build-spec §4): validated-tool suggestions flip status
         * CLICK-driven (node-view accept/reject), never content-derived —
         * accepting one creates a QuestionnaireResponse, not an editor
         * content change, so the content reconciler cannot observe it.
         * (`isSuggestionContentPresent` would return null anyway; the skip is
         * explicit so a future presence check can't silently change this.) */
        if (isValidatedToolOp(record.op)) {
          continue
        }
        const present = isSuggestionContentPresent(
          editor,
          record.op,
          record,
          records
        )
        if (present === null) {
          // Indeterminate: op type unrecognized this version, or the op's target
          // (choice field, heading, anchor text, taskItem) no longer resolves in
          // the doc. Neither is evidence the content was undone — leave the
          // status alone so a renamed heading can't flip an accepted record back
          // to pending. Leave
          // the last-known presence untouched (do NOT record indeterminate).
          continue
        }
        // A genuine absent→present TRANSITION of THIS record's content under an
        // undo/redo op = a redo that re-materialized this accept. Compared
        // against the SYNCHRONOUS last-tick presence (not the async-lagged
        // record.status, which on a rapid undo→redo still reads the stale
        // 'accepted' and would miss the redo). Requiring the transition — not
        // merely "present under an undo/redo op" — is what keeps an UNRELATED
        // undo/redo (which leaves an already-accepted chip present throughout,
        // wasPresent === true) from re-firing the encounter re-link on every
        // keystroke-undo for every accepted chip in the note.
        const wasPresent = presenceRef.current.get(record.id)
        presenceRef.current.set(record.id, present)
        const isRedoOfThisAccept =
          present && isUndoRedoOp && wasPresent === false

        /* The status this record will SETTLE to (see projectedStatusRef): the
         * flip this hook last pushed while it is still in flight, otherwise the
         * server's own value. The projection is retired the moment the server
         * reports it, so a later decision made anywhere else is read fresh. */
        const projected = projectedStatusRef.current.get(record.id)
        if (projected === record.status) {
          projectedStatusRef.current.delete(record.id)
        }
        const effectiveStatus = projected ?? record.status

        if (present && effectiveStatus === 'pending' && !isRedoOfThisAccept) {
          /* This record's content is now in the doc under a forward edit. That
           * is NOT a decision, and nothing is decided here: no status is
           * written, the decoration stays, no side effect runs. `accepted` means
           * an accept action for THIS record — the pipeline projects that
           * directly, holding the exact id the clinician clicked — or a redo of
           * that action. Content equality proves neither: a clinician typing the
           * same sentence, a peer's equivalent edit, and a different record's
           * identical insertion all satisfy it, and the record it used to
           * promote then owned a receipt and an Encounter.diagnosis link for a
           * decision nobody made. Such a suggestion stays pending and stays
           * offered.
           *
           * What IS recorded is the settling status, because the commonest way
           * for a pending record's content to appear is its own accept — and the
           * pipeline's `accepted` write is two round trips from being readable.
           * An undo landing in that window has to find `accepted` here or it
           * refuses the demotion, leaving the record accepted over a document
           * that no longer holds its content with its encounter link stranded.
           * Seeding it costs an already-pending record at most one redundant
           * `pending` write if the clinician undoes their own typing; missing it
           * costs a wrong durable decision. */
          projectedStatusRef.current.set(record.id, 'accepted')
        } else if (isRedoOfThisAccept) {
          // A REDO re-materialized this accept's chip. Drop the now-redundant
          // pending decoration (the one the undo re-painted) so exactly one
          // committed chip remains, and re-establish the FHIR side effects the
          // undo tore down.
          //
          // Delta-guard: only PUSH the status flip when it actually changes the
          // settling status. A no-op accepted→accepted flip is NOT free —
          // setSuggestionStatus re-stamps decidedAt=now() every call and the
          // debounced PATCH only short-circuits on EMPTY ops, so it would drift
          // decidedAt on every such tick. The projection is read rather than
          // record.status because a rapid undo→redo settles faster than the
          // status round trip; the decoration drop and reapply run regardless.
          if (effectiveStatus !== 'accepted') {
            flips.push({ id: record.id, status: 'accepted' })
            projectedStatusRef.current.set(record.id, 'accepted')
          }
          editor.commands.rejectAIProposal(record.id) // removes decoration only
          // Re-establish the FHIR side effects the intervening undo tore down
          // (relink + restore receipt). Idempotent (link-if-absent). A fresh
          // FORWARD accept is not an undo/redo op, so it never reaches this and
          // the pipeline's own side effects are not double-fired.
          onRedoFlipReapply?.(record)
        } else if (
          !present &&
          wasPresent === true &&
          effectiveStatus === 'accepted' &&
          isUndoRedoOp
        ) {
          // A genuine present→absent TRANSITION of THIS record's content under an
          // undo: flip back to pending and re-paint the decoration so the
          // highlight reappears. Forward edits after accept (typing over accepted
          // content) do NOT reach this branch — only genuine Cmd+Z undos do.
          //
          // The transition is required for the same reason the redo path above
          // requires one, and against a live symptom: without it ANY undo demoted
          // EVERY accepted record that merely READ absent, whenever it became so
          // — one Cmd+Z flipped three accepted records to pending at once, cleared
          // their decidedAt, and re-painted them as fresh cards. `wasPresent` is
          // the SYNCHRONOUS last-tick presence, baselined by the hydration pass
          // (see seedPresence) so an undo taken right after a page load — where
          // nothing has ticked yet — still demotes.
          flips.push({ id: record.id, status: 'pending' })
          projectedStatusRef.current.set(record.id, 'pending')
          // Compensate the accept's FHIR side effects (R5): its inserted content
          // is gone, so any encounter link it created is now orphaned. The host
          // callback unlinks it (unlink-only under the no-create model).
          onUndoFlipCompensate?.(record)
          const op = narrowPersistedOp(record.op)
          if (op) {
            const resolved = resolveOperation(
              editor,
              op,
              noteSuggestionsEnabled,
              citationMentionsEnabled
            )
            if (!('error' in resolved)) {
              reAdd.push(
                toAIProposal(
                  record.id,
                  record.provenance.toolCallId,
                  resolved,
                  record.label,
                  record.editedContent
                )
              )
            }
          }
        }
      }
      if (reAdd.length > 0) {
        editor.commands.addAIProposals(reAdd)
      }
      if (flips.length > 0) {
        p.projectStatuses(flips)
      }
    }
    editor.on('transaction', onTransaction)
    return () => {
      editor.off('transaction', onTransaction)
      bumpRemoteWipeGen.cancel()
    }
  }, [
    editor,
    resourceRef,
    revealed,
    hasDocSynced,
    bumpRemoteWipeGen,
    onUndoFlipCompensate,
    onRedoFlipReapply,
    noteSuggestionsEnabled,
    citationMentionsEnabled,
  ])

  // ---- Decided-sweep: drop decorations for no-longer-pending records --------
  /* `pendingSuggestions` is derived from the React-Query-backed staging `Basic`
   * read, so it re-renders when a record is decided or deleted — locally (the
   * tab running a regenerating ask: design symptom S1) OR via cross-tab cache
   * sync (the same practitioner's other tab decides; staging is private per
   * practitioner, so there's no cross-RN convergence to sweep). A painted
   * decoration whose record left the pending set is stale and must be removed
   * in THIS tab without a reload.
   *
   * This effect is intentionally separate from hydration and the reconciler: it
   * NEVER writes status and NEVER paints — it only removes a decoration via
   * `rejectAIProposal(id)` (decoration-only, per the reconciler's use above)
   * when its record is no longer an actionable pending suggestion. So it cannot
   * fight the reconciler's content-derived flips or the re-arm's re-paint
   * (which both keep still-pending records painted).
   *
   * Keyed on the pending id SET signature so it runs exactly when convergence
   * changes the pending set, not on every render. The reconciler's own
   * decoration drop on accept (above) makes most local accepts no-ops here; this
   * catches the cross-tab-sync / run-replace cases the reconciler can't observe.
   *
   * Gated on `revealed` (S5): an unrevealed deferred note has no painted
   * decorations, so there is nothing to sweep; the gate keeps the dormant tab
   * inert until the banner reveals the apparatus. */
  const pendingIdSignature = useMemo(
    () =>
      persistence.pendingSuggestions
        .map((s) => s.id)
        .sort()
        .join('|'),
    [persistence.pendingSuggestions]
  )
  // resourceRef is the reset key — the effect must rerun when the resource changes even though the callback never reads it.
  useEffect(() => {
    if (!editor || !resourceRef || !revealed) {
      return
    }
    const pluginState = AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)
    if (!pluginState || pluginState.proposals.size === 0) {
      return
    }
    const pendingIds = new Set(
      persistenceRef.current.pendingSuggestions.map((s) => s.id)
    )
    for (const id of pluginState.proposals.keys()) {
      if (!pendingIds.has(id)) {
        // Decided remotely/locally, or deleted by a new run: sweep the stale
        // decoration. `getLatestStatus` is consulted so a record whose decide
        // is still propagating but already locally pending is NOT swept.
        /* The PROJECTION first, then the persisted read — the same order the
         * reconciler uses, and the only one that answers the question this line
         * is asking. `getLatestStatus` reads the staging Basic, which a flip
         * reaches two round trips late, so between an accept and the Cmd+Z that
         * takes it back it still reports `accepted` while the demotion is in
         * flight. Measured live: the reconciler demoted an undone record and
         * re-painted its card, and this swept that card a beat later on the
         * stale read. The record settled correctly to pending with no card
         * offering it, and only a reload brought it back. */
        const latest =
          projectedStatusRef.current.get(id) ??
          persistenceRef.current.getLatestStatus(id)
        if (latest !== 'pending') {
          editor.commands.rejectAIProposal(id)
        }
      }
    }
  }, [editor, resourceRef, revealed, pendingIdSignature])

  // ---- Reveal-time condition validation (Option C Card 1) -------------------
  /* The PRIMARY accept-integrity guard: after every reveal/hydration paint, an
   * async pass over the pending condition-mention suggestions batch-reads each
   * referenced condition's existence + current clinical status and UN-PAINTS any
   * whose condition is missing/hallucinated or no longer active — routing it to
   * the same dropped list (with a clinical reason) instead of leaving it painted
   * as an acceptable highlight, so an invalid suggestion never reads as
   * acceptable. Paint stays instant and untouched (this never rewrites
   * paintPendingProposals or the undo-repaint's same-tick guarantee); the check
   * is a fast-follow that removes the decoration a beat later.
   *
   * Reads what paint reads — record.op via resolveOperation → resolved.htmlContent
   * (NOT record.editedContent; an island edit that swapped a condition before
   * closing without accepting is a pre-existing paint gap, out of Card 1 scope).
   * Batched (N mentions → one _id search per uncached batch) and cached per hook
   * instance (a condition id is read at most once per lifetime). Card 2's
   * accept-time backstop covers the reveal→click freshness gap the cache leaves.
   *
   * Dormant unless a `medplum` client is threaded in — every non-condition note
   * and every existing positional caller behaves exactly as today. */
  const conditionValidityCacheRef = useRef(new Map<string, ConditionValidity>())

  /* Doc-content version: a monotonic counter bumped on every content-changing
   * transaction, so the candidate memo below re-resolves as the doc hydrates.
   * On a COLD reveal the collaborative (Yjs) doc has NOT yet populated the note
   * body, so resolveOperation can't find the target section and
   * `conditionCandidates` computes EMPTY; the memo's other deps (editor,
   * pendingSuggestions) don't change when the CRDT content arrives a beat later,
   * so without this signal it would stay empty forever and reveal-validation
   * would never run — the B1 cold-reveal hydration race. Bumping on docChanged
   * makes candidate resolution re-run once the section appears (for ANY op whose
   * target isn't in the hydrated doc yet, not just one heading). Mounted only
   * when the validation feature is active (`medplum` threaded) and bumps only
   * while suggestions are pending, so non-condition notes and settled notes pay
   * nothing. Decoration-only commands (paint/un-paint) don't set docChanged, so
   * this never self-triggers. */
  const [docContentVersion, setDocContentVersion] = useState(0)
  useEffect(() => {
    if (!editor || !medplum) {
      return
    }
    const onTransaction = ({
      transaction,
    }: EditorEvents['transaction']): void => {
      if (!transaction.docChanged) {
        return
      }
      if (persistenceRef.current.pendingSuggestions.length === 0) {
        return
      }
      setDocContentVersion((v) => v + 1)
    }
    editor.on('transaction', onTransaction)
    return () => {
      editor.off('transaction', onTransaction)
    }
  }, [editor, medplum])

  /* The pending condition-mention suggestions that WOULD paint (resolve
   * cleanly), each with the condition ids it references. Placement failures are
   * excluded — paint already surfaced those as dropped — so this covers only
   * highlights that would otherwise read as acceptable. Empty until the editor
   * is ready or when no pending suggestion tags a condition. */
  const conditionCandidates = useMemo<ConditionCandidate[]>(() => {
    if (!editor) {
      return []
    }
    const candidates: ConditionCandidate[] = []
    for (const rec of persistence.pendingSuggestions) {
      if (isValidatedToolOp(rec.op)) {
        continue
      }
      const op = narrowPersistedOp(rec.op)
      if (!op) {
        continue
      }
      const resolved = resolveOperation(
        editor,
        op,
        noteSuggestionsEnabled,
        citationMentionsEnabled
      )
      if ('error' in resolved) {
        continue
      }
      const conditions = collectHtmlReferencedConditions(
        editor,
        resolved.htmlContent
      )
      if (conditions.size > 0) {
        candidates.push({
          id: rec.id,
          label: rec.label ?? 'Suggestion',
          content: droppedSuggestionContent(rec),
          conditionIds: [...conditions.keys()],
          displayById: conditions,
        })
      }
    }
    return candidates
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- docContentVersion is an intentional recompute trigger — it carries no value the body reads, but bumping it on content-changing transactions is what makes candidate resolution re-run once the collaborative doc hydrates the target section (the B1 cold-reveal race); editor + pendingSuggestions are the read deps.
  }, [
    editor,
    persistence.pendingSuggestions,
    docContentVersion,
    noteSuggestionsEnabled,
    citationMentionsEnabled,
  ])

  /* A content signature over the candidate set: each candidate's record id plus
   * its (sorted) referenced condition ids. `conditionCandidates` recomputes on
   * every content-changing transaction (via docContentVersion) and hands back a
   * NEW array each time, so the validation effect keys its re-run on this string,
   * NOT the array identity — a keystroke (or hydration tick) that changes no
   * candidate's referenced conditions never re-fires and cancels the in-flight
   * FHIR read. Mirrors reconcileSignature / pendingIdSignature below and above. */
  const conditionCandidatesSignature = useMemo(
    () =>
      conditionCandidates
        .map((c) => `${c.id}:${[...c.conditionIds].sort().join(',')}`)
        .sort()
        .join('|'),
    [conditionCandidates]
  )

  useEffect(() => {
    if (!editor || !resourceRef || !revealed || !medplum) {
      return
    }
    if (conditionCandidates.length === 0) {
      return
    }
    const cache = conditionValidityCacheRef.current
    const uncachedIds = [
      ...new Set(conditionCandidates.flatMap((c) => c.conditionIds)),
    ].filter((id) => !cache.has(id))
    let cancelled = false
    const run = async (): Promise<void> => {
      if (uncachedIds.length > 0) {
        /* Fail open on a transient read failure: leave the ids uncached (a
         * later run retries) and un-paint nothing. Dropping on a network error
         * would attach a false clinical explanation to every painted mention. */
        let fresh: Map<string, ConditionValidity>
        try {
          fresh = await readConditionValidity(
            medplum,
            uncachedIds,
            patientRef ?? null
          )
        } catch {
          return
        }
        if (cancelled) {
          return
        }
        for (const [id, validity] of fresh) {
          cache.set(id, validity)
        }
      }
      if (cancelled) {
        return
      }
      const pluginState = AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)
      const drops: DroppedSuggestion[] = []
      for (const candidate of conditionCandidates) {
        let reason: string | undefined
        for (const conditionId of candidate.conditionIds) {
          const validity = cache.get(conditionId)
          if (validity && !validity.valid) {
            // Named with THIS suggestion's display text, not whichever other
            // suggestion happened to populate the shared validity cache.
            const display = candidate.displayById.get(conditionId)
            reason =
              validity.kind === 'missing'
                ? conditionMissingDropReason(display)
                : validity.kind === 'notOnChart'
                  ? conditionNotOnChartDropReason(display)
                  : conditionInactiveDropReason(display)
            break
          }
        }
        if (reason === undefined) {
          continue
        }
        /* Un-paint decoration-only (like the reconciler/sweep's
         * rejectAIProposal), and only when it's actually painted, so repeated
         * effect runs never re-dispatch. The record stays pending — a decoration
         * drop, not a decide — so the dropped-list re-derive below keeps it. */
        if (pluginState?.proposals.has(candidate.id)) {
          editor.commands.rejectAIProposal(candidate.id)
        }
        drops.push({
          id: candidate.id,
          label: candidate.label,
          content: candidate.content,
          reason,
        })
      }
      if (cancelled || drops.length === 0) {
        return
      }
      setDropped((prev) => {
        const seen = new Set(prev.map((d) => d.id))
        const additions = drops.filter((d) => !seen.has(d.id))
        return additions.length === 0 ? prev : [...prev, ...additions]
      })
    }
    void run()
    return () => {
      cancelled = true
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- contentResetCount/remoteWipeGen/revealGen are intentional re-run triggers — each drives a fresh paint (host reset, remote-wipe re-arm, hide→re-reveal), so the validation must re-run to un-paint any now-invalid mention the repaint restored. conditionCandidates is read from the closure but the re-run is keyed on conditionCandidatesSignature (its content projection), so a content-changing transaction that doesn't alter the candidate set never re-triggers/cancels the run; when the effect does run it always sees the latest render's conditionCandidates. The rest of the body reads the listed deps.
  }, [
    editor,
    resourceRef,
    revealed,
    medplum,
    conditionCandidatesSignature,
    contentResetCount,
    remoteWipeGen,
    revealGen,
    patientRef,
  ])

  // ---- Reconcile sweep (Option C Card 3) ------------------------------------
  /* The self-healing backstop that makes the sign gate unnecessary: after every
   * reveal it drives each receipt-bearing (accepted) suggestion to convergence —
   * unlinking a stranded encounter link an undo left orphaned (the FHIR unlink
   * runUndoFlipCompensation's catch swallowed), and retrying a draft-flagged node
   * whose side effect never versioned its reference. Both directions reuse the
   * verified accept/undo machinery, are idempotent (a converged receipt is a
   * no-op), sibling-safe, and receipt-owned-only — see runReconcileSweep.
   *
   * Keyed on a signature of receipt-bearing ids + their created refs, so it runs
   * exactly when convergence changes the receipt state, not on every render (the
   * decided-sweep's shape). Gated on `revealed` (S5) — like every other effect
   * here, a dormant/unrevealed note has nothing to reconcile — and dormant unless
   * a `medplum` client is threaded in, so non-condition callers are unaffected.
   * The revealed gate also means the sweep can't reach a signed note (its reveal
   * apparatus never latches); the pre-sign drain in SignButton is the belt-and-
   * suspenders that sweeps a just-created orphan before signing. */
  const reconcileSignature = useMemo(() => {
    const parts: string[] = []
    for (const record of persistence.getAllSuggestions()) {
      const receipt = persistence.getReceipt(record.id)
      if (!receipt) {
        continue
      }
      const refs = Object.values(receipt.createdRefs ?? {})
        .sort()
        .join(',')
      parts.push(`${record.id}:${refs}`)
    }
    return parts.sort().join('|')
  }, [persistence])

  useEffect(() => {
    if (!editor || !resourceRef || !revealed || !medplum) {
      return
    }
    /* Hydration gate: the sweep reads `editor.state.doc` to decide whether a
     * receipt's created condition links are still referenced by content, and
     * UNLINKS (FHIR) + clears the receipt for any it finds orphaned. On a cold
     * reload the collaborative doc has NOT yet synced, so `editor.state.doc` is
     * transiently empty — every committed accept would read as orphaned and be
     * destructively unlinked (real data loss: Encounter.diagnosis vanishes,
     * live-confirmed reading an empty doc ~1s before the first hydration
     * transaction). Waiting for the provider's "synchronized" status means the
     * sweep only ever judges orphans against the real, hydrated doc; a genuine
     * post-hydration orphan (content removed AFTER sync) still heals, since the
     * effect re-runs when this flips true. (runReconcileSweep also self-guards
     * against a wholly-empty doc as belt-and-suspenders.) */
    if (!isDocSynchronized) {
      return
    }
    if (reconcileSignature === '') {
      return
    }
    let cancelled = false
    const run = async (): Promise<void> => {
      await runReconcileSweep({
        editor,
        persistence: persistenceRef.current,
        medplum,
        resourceRef,
        patientRef: patientRef ?? null,
        noteSuggestionsEnabled,
        citationMentionsEnabled,
        onDropped: (d) => {
          if (cancelled) {
            return
          }
          setDropped((prev) =>
            prev.some((x) => x.id === d.id) ? prev : [...prev, d]
          )
        },
      })
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [
    editor,
    resourceRef,
    revealed,
    medplum,
    reconcileSignature,
    isDocSynchronized,
    noteSuggestionsEnabled,
    citationMentionsEnabled,
    patientRef,
  ])

  // ---- Dropped-list re-derive: keep the dropped list in step with pending ----
  /* The dropped list is a snapshot from the last paint — a record that couldn't
   * be placed but is still pending. When the clinician Dismisses one (or it is
   * otherwise decided), it leaves the pending set, but the snapshot would keep
   * showing it until the next full paint (the 1.1b lingering-Dismiss bug). Keyed
   * on the pending id signature so it re-derives exactly when convergence changes
   * the set; returns the prior array unchanged when nothing dropped, so it never
   * forces a needless re-render. Runs ungated on `revealed`: a hidden note has an
   * empty dropped list already, so the filter is a no-op there. */
  // pendingIdSignature is the intentional re-run trigger; the body reads persistenceRef, not the signature.
  useEffect(() => {
    const pendingIds = new Set(
      persistenceRef.current.pendingSuggestions.map((s) => s.id)
    )
    setDropped((prev) => {
      const next = prev.filter((d) => pendingIds.has(d.id))
      return next.length === prev.length ? prev : next
    })
  }, [pendingIdSignature])

  // ---- Recovered drops: re-probe the dropped list as the note is edited ------
  /* Drop resolution otherwise happens once, at reveal. When the clinician then
   * FIXES the note — deletes the duplicate sentence that made an anchor
   * ambiguous, retypes text that had gone missing — the suggestion stays
   * dropped and its row keeps stating a reason the note no longer bears out.
   *
   * So re-probe the dropped records on a debounced document change and hand the
   * placeable ones a `place` callback. Painting is never automatic: a card
   * appearing under the caret mid-edit is surprise the clinician didn't ask
   * for, so the status bar surfaces one control and the click is theirs.
   *
   * Deliberately NOT folded into `docContentVersion` above, which is gated on
   * `medplum` being threaded so non-condition notes pay nothing; widening that
   * gate would make every pending note re-resolve every record on every
   * keystroke. This listener bumps only while there is something to re-probe --
   * a dropped record or a painted card -- and only after the flurry settles. */
  const droppedRef = useRef(dropped)
  droppedRef.current = dropped
  const [recoveryProbe, setRecoveryProbe] = useState(0)
  const bumpRecoveryProbe = useDebouncedCallback(() => {
    if (mountedRef.current) {
      setRecoveryProbe((v) => v + 1)
    }
  }, 300)
  useEffect(() => () => bumpRecoveryProbe.cancel(), [bumpRecoveryProbe])

  useEffect(() => {
    if (!editor || !noteSuggestionsEnabled) {
      return
    }
    const onTransaction = ({
      transaction,
    }: EditorEvents['transaction']): void => {
      if (!transaction.docChanged) {
        return
      }
      /* Any pending record is something to re-probe, not just a dropped row:
       * the probe runs in both directions, and the edit that strands a record
       * can leave it neither painted nor dropped (see the lost-placement effect
       * below). Re-resolving is per settled burst, not per keystroke -- the
       * debounce is what keeps this off the typing path. */
      if (
        droppedRef.current.length === 0 &&
        persistenceRef.current.pendingSuggestions.length === 0
      ) {
        return
      }
      bumpRecoveryProbe()
    }
    editor.on('transaction', onTransaction)
    return () => {
      editor.off('transaction', onTransaction)
      bumpRecoveryProbe.cancel()
    }
  }, [editor, noteSuggestionsEnabled, bumpRecoveryProbe])

  // ---- Lost placements: un-paint a card whose target left the note ---------
  /* The mirror of the recovery probe, on the same beat. A painted card whose
   * anchor text the clinician has since deleted keeps rendering: it is counted
   * as pending AND (once probed) as dropped, so the banner contradicts itself,
   * and the Accept it still offers can only fail. A reload already resolves
   * this correctly -- this is what makes the live document agree with the
   * reloaded one instead of the answer depending on whether you refreshed.
   *
   * Driven off the PENDING RECORDS rather than the painted proposals, because
   * the two editor kinds strand such a record in opposite states. On an
   * ordinary local edit the plugin drops the decoration itself, leaving the
   * record neither painted nor dropped -- it just disappears. In a
   * collaborative note the same keystroke arrives as a whole-doc replace, so a
   * proposal carrying a relative anchor is KEPT and flagged `stale` for the
   * anti-flash window; when its anchor is genuinely gone rather than merely
   * collapsed, nothing retires it and it stays painted for good. Walking the
   * records catches both.
   *
   * `stale` is deliberately NOT skipped, and that is safe because this asks the
   * question by CONTENT -- does the anchor text still exist -- not by position.
   * A whole-doc replace collapses positions while leaving the text alone, so a
   * transient sync resolves cleanly here and the card is left painted. Only a
   * genuine deletion drops it. The 300ms debounce is the second guard.
   *
   * Un-painting is decoration-only (`rejectAIProposal`, the same way the
   * pending-set sweep and the condition-validity pass use it), so the record
   * stays pending: undo the deletion and the recovery half of this probe offers
   * to place it again. */
  useEffect(() => {
    if (!editor || !noteSuggestionsEnabled || !revealed) {
      return
    }
    const pluginState = AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)
    const lost: DroppedSuggestion[] = []
    for (const record of persistenceRef.current.pendingSuggestions) {
      if (isValidatedToolOp(record.op)) {
        continue
      }
      const op = narrowPersistedOp(record.op)
      if (!op) {
        continue
      }
      const probe = resolveOperation(
        editor,
        op,
        noteSuggestionsEnabled,
        citationMentionsEnabled
      )
      if (!('error' in probe)) {
        continue
      }
      /* An unresolvable anchor is not enough to call the record lost: an accept
       * CONSUMES its own anchor, so mid-settle — while the status write is still
       * two round trips from being readable and the record therefore still walks
       * this list as "pending" — an accepted record is indistinguishable from a
       * deleted-anchor one by placement alone. Ask the reconciler's own presence
       * question: content in the doc means the record is APPLIED, not lost, and
       * painting it as "couldn't be placed" was the measured banner flash (a
       * false drop notice for each just-accepted record until the server caught
       * up). Only a definite `true` skips — indeterminate is not evidence the
       * accept applied, and a genuinely deleted anchor stays a drop. */
      if (isSuggestionContentPresent(editor, record.op, record) === true) {
        continue
      }
      if (pluginState?.proposals.has(record.id)) {
        editor.commands.rejectAIProposal(record.id)
      }
      lost.push(
        droppedFor(
          editor,
          record,
          noteSuggestionsEnabled,
          citationMentionsEnabled
        )
      )
    }
    if (lost.length === 0) {
      return
    }
    setDropped((prev) => {
      const seen = new Set(prev.map((d) => d.id))
      const additions = lost.filter((entry) => !seen.has(entry.id))
      return additions.length === 0 ? prev : [...prev, ...additions]
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- recoveryProbe is the intentional re-run trigger; the body reads persistenceRef, not it.
  }, [editor, noteSuggestionsEnabled, citationMentionsEnabled, recoveryProbe])

  /* Paint ONE recovered record and retire its dropped row. Deliberately not a
   * whole `paintPendingProposals` pass, which recomputes the entire dropped list
   * and would discard the condition-validity drops the validation effect
   * appended. The op is re-resolved here rather than captured at probe time: the
   * doc can move between the probe and the click, and a stale resolution would
   * paint at the wrong place. */
  const placeRecovered = useCallback(
    (id: string): void => {
      if (!editor) {
        return
      }
      const record = persistenceRef.current.pendingSuggestions.find(
        (candidate) => candidate.id === id
      )
      if (!record) {
        return
      }
      const op = narrowPersistedOp(record.op)
      if (!op) {
        return
      }
      const resolved = resolveOperation(
        editor,
        op,
        noteSuggestionsEnabled,
        citationMentionsEnabled
      )
      if ('error' in resolved) {
        return
      }
      editor.commands.addAIProposals([
        toAIProposal(
          record.id,
          record.provenance.toolCallId,
          resolved,
          record.label,
          record.editedContent
        ),
      ])
      setDropped((prev) => prev.filter((entry) => entry.id !== id))
    },
    [editor, noteSuggestionsEnabled, citationMentionsEnabled]
  )

  /* The dropped list's membership, so a drop appended AFTER this effect last ran
   * (the reveal paint, the async validation pass, the reconcile sweep) is probed
   * too. Safe as a dependency where `dropped` itself would loop: the effect only
   * ever changes an entry's `place`, never the id set. */
  const droppedIdSignature = useMemo(
    () =>
      dropped
        .map((entry) => entry.id)
        .sort()
        .join('|'),
    [dropped]
  )

  // resourceRef is the reset key — the effect must rerun when the resource changes even though the callback never reads it.
  useEffect(() => {
    if (!editor || !noteSuggestionsEnabled) {
      return
    }
    const recovered = recoveredDropIds(
      editor,
      droppedRef.current,
      persistenceRef.current,
      noteSuggestionsEnabled,
      citationMentionsEnabled
    )
    // Write only when the recovered SET actually moved: `dropped` feeds a
    // transaction listener and several effects, so a fresh array on every probe
    // would churn them for nothing.
    setDropped((prev) => {
      let changed = false
      const next = prev.map((entry) => {
        const canPlace = recovered.has(entry.id)
        if (canPlace === (entry.place !== undefined)) {
          return entry
        }
        changed = true
        return {
          ...entry,
          place: canPlace ? () => placeRecovered(entry.id) : undefined,
        }
      })
      return changed ? next : prev
    })
  }, [
    editor,
    noteSuggestionsEnabled,
    citationMentionsEnabled,
    placeRecovered,
    recoveryProbe,
    droppedIdSignature,
  ])

  return dropped
}

interface UseUndoRejectRepaintResult {
  repaintGen: number
  requestRepaint: (ids: readonly string[]) => void
}

/*
 * The repaint generation the collaborative host feeds back into hydration, and
 * the request that bumps it.
 *
 * Split out from CollaborativeEditor so the TIMING is unit-testable: the bump
 * cannot happen when the clinician clicks "Undo rejection of all", because
 * `projectStatuses` is a version-checked PATCH followed by an invalidated
 * staging refetch — two round trips — and a pass re-armed at click time runs
 * while the records still read `rejected`, paints nothing, and re-consumes the
 * one-shot at the new key. Nothing would re-arm when they finally turn
 * `pending`. Live symptom, 2026-08-19 pair QA: the undo persisted (a reload
 * showed the suggestions staged again) while the editor stayed blank.
 *
 * So the request records WHICH ids it is waiting for, and the bump fires on the
 * pass where every one of them has actually arrived in the pending set.
 *
 * KNOWN GAP, deliberately not closed here: a Reject All followed immediately by
 * an Undo asks while the reject write is still in flight, so the ids never leave
 * the pending set and no arrival is ever observed — that ordering still needs a
 * reload. Treating "already pending" as arrival was tried and is WRONG: it
 * repaints, then the reject write lands and the decided sweep un-paints, and the
 * later undo write finds this latch consumed with nothing waiting. The check
 * cannot be made sound from status alone, because status is not readable until
 * the write round-trips; closing it needs local intent to be readable
 * synchronously, which is Notion ticket TC-711.
 */
export function useUndoRejectRepaint(
  pendingIds: readonly string[]
): UseUndoRejectRepaintResult {
  const [repaintGen, setRepaintGen] = useState(0)
  const awaitingRef = useRef<readonly string[] | null>(null)
  const requestRepaint = useCallback((ids: readonly string[]) => {
    if (ids.length > 0) {
      awaitingRef.current = ids
    }
  }, [])
  /* Read through a ref so the effect compares against the real array while
   * still being TRIGGERED by the signature; splitting the joined string back
   * apart mishandles an empty id. */
  const pendingIdsRef = useRef(pendingIds)
  pendingIdsRef.current = pendingIds
  const pendingSignature = pendingIds.join(',')
  // resourceRef is the reset key — the effect must rerun when the resource changes even though the callback never reads it.
  useEffect(() => {
    const awaiting = awaitingRef.current
    if (!awaiting) {
      return
    }
    const arrived = new Set(pendingIdsRef.current)
    if (!awaiting.every((id) => arrived.has(id))) {
      return
    }
    awaitingRef.current = null
    setRepaintGen((gen) => gen + 1)
  }, [pendingSignature])
  return { repaintGen, requestRepaint }
}

// The collaborative host's typed entry point. `isDocSynchronized` on
// the hook above defaults to `true` — deliberately, so the many non-collab
// callers (tests, the two-client harness) can stay terse — but that same
// default means CollaborativeEditor.tsx silently reverting to "always
// synchronized" if its wiring is ever deleted would compile and pass every
// existing test (they all pass the flag directly). This wrapper is CollaborativeEditor's
// ONLY call surface: every parameter is REQUIRED (no defaults), so omitting
// `isDocSynchronized` — or any argument after it — is a straight `tsc` error,
// the same guarantee `useMountReconcile`'s required `isDocSynchronized` field
// already gives that hook. Pure pass-through; no new behavior.
export function useAISuggestionHydrationForCollabHost(
  editor: Editor | null,
  isEditorReady: boolean,
  resourceRef: string | undefined,
  persistence: SuggestionPersistence,
  noteSuggestionsEnabled: boolean,
  citationMentionsEnabled: boolean,
  contentResetCount: number,
  revealed: boolean,
  revealGen: number,
  onUndoFlipCompensate: ((record: StagedSuggestion) => void) | undefined,
  medplum: SeenMedplumClient | undefined,
  isDocSynchronized: boolean,
  onRedoFlipReapply: ((record: StagedSuggestion) => void) | undefined,
  patientRef: string | null,
  repaintGen: number
): DroppedSuggestion[] {
  return useAISuggestionHydration(
    editor,
    isEditorReady,
    resourceRef,
    persistence,
    noteSuggestionsEnabled,
    citationMentionsEnabled,
    contentResetCount,
    revealed,
    revealGen,
    onUndoFlipCompensate,
    medplum,
    isDocSynchronized,
    onRedoFlipReapply,
    patientRef,
    repaintGen
  )
}

// A pending condition-mention suggestion the reveal-time validation pass may
// need to un-paint: its record id + label for the dropped list, and the
// condition ids its resolved content tags (version-agnostic bare ids).
interface ConditionCandidate {
  id: string
  label: string
  // The suggestion's own text, carried so an un-painted candidate becomes a
  // fully-described drop without re-reading the record.
  content?: string
  conditionIds: string[]
  // Display text per referenced condition id, so a refusal names the condition.
  displayById: Map<string, string | undefined>
}

// One condition's reveal-time verdict. Discriminated so an invalid verdict
// always carries the clinical reason surfaced on the dropped list.
// The KIND of invalidity, never a rendered reason: the copy names the condition
// using the display text of the suggestion being explained, and two suggestions
// can tag one condition with different display text.
type ConditionValidity =
  | { valid: true }
  | { valid: false; kind: 'missing' | 'inactive' | 'notOnChart' }

/*
 * Batch-read the reveal-time validity of a set of condition ids in ONE FHIR
 * search (the shared `referencesQuery` `_id` batch primitive), classifying each:
 * not returned → missing/hallucinated; returned but not a current clinical
 * status → resolved/inactive; otherwise valid. Callers cache the result per hook
 * instance and only pass ids not already cached, so N mentions cost at most one
 * read. Empty input is a no-op (never issues a bare `_id=` search, which would
 * match every condition).
 */
async function readConditionValidity(
  medplum: SeenMedplumClient,
  ids: string[],
  // The note's participant (`Patient/{id}`); null skips the ownership check.
  patientRef: string | null
): Promise<Map<string, ConditionValidity>> {
  const validity = new Map<string, ConditionValidity>()
  if (ids.length === 0) {
    return validity
  }
  const conditions = await medplum.searchResourceClass(
    ExtendedCondition,
    ExtendedCondition.referencesQuery(
      ids.map((id) => ({ reference: `Condition/${id}` }))
    )
  )
  const byId = new Map(conditions.map((condition) => [condition.id, condition]))
  for (const id of ids) {
    const condition = byId.get(id)
    if (!condition) {
      validity.set(id, { valid: false, kind: 'missing' })
    } else if (
      patientRef !== null &&
      (condition.participantSubject?.reference === undefined ||
        referenceStringToId(condition.participantSubject.reference) !==
          referenceStringToId(patientRef))
    ) {
      // Ownership before status, as in checkConditionPrecondition: a
      // wrong-chart condition's clinical status is irrelevant.
      validity.set(id, { valid: false, kind: 'notOnChart' })
    } else if (
      !ExtendedCondition.isCurrentClinicalStatus(condition.clinicalStatus)
    ) {
      validity.set(id, { valid: false, kind: 'inactive' })
    } else {
      validity.set(id, { valid: true })
    }
  }
  return validity
}
